<template>
  <SubletPage v-if="brokers" :categories="brokers" title="Mäklarens uppgifter">
    <OvForm
      :is-loading="savingApplicationData"
      :form-has-changed="formHasChanged"
      :form-type="formType"
      v-on="$listeners"
    >
      <FieldsWrapper>
        <OvOrganizationSearch v-model="brokers[0].organizationNumber" />
        <CompanyDetails :company-details="activeCompanyDetails" />
      </FieldsWrapper>
      <FieldsWrapper>
        <Fieldset>
          <OvInput
            v-model="brokers[0].name"
            label="Mäklarens fullständiga namn"
            placeholder="Mäklarens fullständiga namn"
            description="Ange mäklarens fullständiga namn"
            autocomplete="given-name"
          />
          <OvInput
            v-model="brokers[0].ssn"
            label="Mäklarens personnummer"
            placeholder="Mäklarens personnummer"
            description="Ange mäklarens personnummer"
            :validation="validateSsn"
            :error-text="ssnErrorText"
          />
        </Fieldset>
        <Fieldset>
          <OvInput
            v-model="brokers[0].email"
            label="Mäklarens e-post"
            placeholder="E-post"
            description="Ange mäklarens e-postadress"
            type="email"
            :validation="validEmail"
            error-text="E-postadress måste anges på detta vis: adress@mailhost.com"
            autocomplete="email"
          />
          <OvInput
            v-model="brokers[0].phone"
            label="Mäklarens telefonnummer"
            placeholder="Telefonnummer"
            description="Ange mäklarens telefonnummer"
            inputmode="numeric"
            pattern="[0-9]*"
            autocomplete="tel"
          />
        </Fieldset>
        <OvInput
          v-model="brokers[0].address"
          label="Mäklarens adress"
          placeholder="Mäklarens adress"
          description="Ange mäklarens adress"
        />
        <Fieldset>
          <OvPostalCodeSearchBar
            v-model="brokers[0].postalCode"
            :class="{ autocomplete: postalCodeCompleted }"
            label="Mäklarens postnummer"
            placeholder="Mäklarens postnummer"
            description="Ange mäklarens postnummer"
            :validation="validatePostalCode"
            error-text="Postnummer måste anges med endast 5 siffror"
            @input="postalCodeCompleted = false"
            @dropdownMatch="postalCodeDropdownMatch($event, 0)"
          />
          <OvInput
            v-model="brokers[0].city"
            :class="{ autocomplete: postalCodeCompleted }"
            label="Mäklarens ort"
            placeholder="Mäklarens ort"
            description="Ange mäklarens ort"
            @input="postalCodeCompleted = false"
          />
        </Fieldset>
      </FieldsWrapper>
    </OvForm>
  </SubletPage>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import {
  validEmail,
  validateNumber,
  validatePersonalNumber,
  validateOrganizationNumber,
  validatePostalCode
} from '@/utils/input-validations';
import { scrollExpandableForm } from '@/utils/scroll';

import Fieldset from '@/components/Fieldset.vue';
import OvInput from '@/components/OvInput.vue';
import OvForm from '@/components/OvForm.vue';
import SubletPage from '@/components/SubletPage.vue';
import FieldsWrapper from '@/components/FieldsWrapper.vue';
import CompanyDetails from '@/components/CompanyDetails.vue';
import OvOrganizationSearch from '@/components/OvOrganizationSearch.vue';
import OvPostalCodeSearchBar from '@/components/OvPostalCodeSearchBar.vue';

export default {
  name: 'Brokers',
  components: {
    Fieldset,
    OvInput,
    OvForm,
    SubletPage,
    FieldsWrapper,
    CompanyDetails,
    OvOrganizationSearch,
    OvPostalCodeSearchBar
  },
  props: {
    application: {
      type: Object,
      required: true
    },
    savingApplicationData: {
      type: Boolean,
      required: true
    },
    formHasChanged: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      validEmail,
      validateNumber,
      validateOrganizationNumber,
      ssnErrorText:
        'Personnummer måste anges med 10 siffror, t.ex. YYMMDD-XXXX',
      confirmModalVisible: false,
      formType: 'brokers',
      validatePostalCode,
      isSearchingPostalCode: false,
      postalCodeCompleted: false,
      secondPostalCodeCompleted: false
    };
  },
  computed: {
    ...mapState({
      companies: state => state.companies.companies
    }),
    ...mapGetters({
      isMobile: 'app/isMobile',
      applicationIsEditable: 'ovApplication/applicationIsEditable',
      rejectedCategories: 'ovApplication/rejectedCategories'
    }),
    activeCompanyDetails() {
      if (
        !this.validateOrganizationNumber(this.brokers[0]?.organizationNumber)
      ) {
        return {};
      }
      return this.companies[
        this.brokers[0].organizationNumber.split('-').join('')
      ];
    },
    brokers: {
      get() {
        return this.application?.brokers;
      },
      set() {
        this.$emit();
      }
    },
    brokersCategoryRejected() {
      return this.rejectedCategories.includes('brokers');
    }
  },
  methods: {
    ...mapActions({
      fetchCompany: 'companies/fetchCompany'
    }),
    postalCodeDropdownMatch(match, index) {
      this.$emit('isEditing', this.formType);
      this.brokers[index].postalCode = match.name;
      this.brokers[index].city = match.description;
      if (index === 0) {
        this.postalCodeCompleted = true;
      } else {
        this.secondPostalCodeCompleted = true;
      }
    },
    async confirmModalClick(eventType) {
      if (eventType === 'CONFIRM') {
        //this.brokers.pop();
        this.$delete(this.brokers, 1);
        this.emitEditing();
      }
      this.confirmModalVisible = false;
    },
    scroll() {
      const formInput = this.$refs.expandableForm;
      const targetId = 'sticky-text-container';

      scrollExpandableForm(formInput, targetId);
    },
    validateSsn(value) {
      if (!validatePersonalNumber(value)) {
        this.ssnErrorText = 'Det personnummer du angivit är inte giltigt';
        return false;
      }
      return validatePersonalNumber(value);
    },
    emitEditing() {
      this.$emit('isEditing', this.formType);
    }
  }
};
</script>
<style lang="scss" scoped>
.autocomplete :deep(input) {
  background-color: #e8f0fe;
}
.top-container {
  display: flex;
  align-items: center;

  .co-tenant-info-modal {
    margin-bottom: 0.3rem;
  }

  .text-info {
    padding: 1rem 0;
  }
}
.c-checkbox {
  margin-bottom: 0 !important;
  // @include mobile {
  //   margin-bottom: 4.8rem;
  // }
  &.active {
    margin-bottom: 2.4rem !important;
  }
}
</style>
